'use client';

import { useEffect, useMemo, useReducer } from 'react';

import { useGetTasksQuery } from '@/features/tasks/api/tasksApi';
import { useWorkspaceRoutes } from '@/hooks/useWorkspaceRoutes';
import { formatDueDateLabel } from '@/utils/tasks';
import { Avatar, EmptyState, InfiniteTable, Link, Tooltip } from '@web/ui';
import { notify } from '@web/utils';

import styles from './AllJobTasksList.module.scss';

import type { Task, TaskStatus } from '@/features/tasks/types';

type AllJobTasksStatusFilter =
  | 'all'
  | 'draft'
  | 'backlog'
  | 'inprogress'
  | 'completed'
  | 'archived';

type AllJobTasksListProps = {
  search: string;
  status: AllJobTasksStatusFilter;
};

type AllJobTasksState = {
  page: number;
  tasks: Task[];
  nextPage: number | null;
  filterKey: string;
};

type AllJobTasksAction =
  | { type: 'reset'; filterKey: string }
  | { type: 'setPage'; page: number }
  | {
      type: 'mergePage';
      filterKey: string;
      page: number;
      rows: Task[];
      nextPage: number | null;
    };

const PAGE_SIZE = 100;

function getNextPage(nextUrl: string | null) {
  if (!nextUrl) {
    return null;
  }

  try {
    const parsed = nextUrl.startsWith('http')
      ? new URL(nextUrl)
      : new URL(nextUrl, 'http://localhost');
    const pageValue = parsed.searchParams.get('page');
    if (!pageValue) {
      return null;
    }
    const page = Number(pageValue);
    return Number.isNaN(page) ? null : page;
  } catch {
    return null;
  }
}

function dedupeById(rows: Task[]) {
  const byId = new Map<string, Task>();
  rows.forEach((row) => byId.set(row.id, row));
  return Array.from(byId.values());
}

function getTaskStatusLabel(task: Task) {
  if (task.is_draft) {
    return 'Draft';
  }

  if (task.status === 'inprogress') {
    return 'In Progress';
  }

  return task.status.charAt(0).toUpperCase() + task.status.slice(1);
}

function getTaskJobId(task: Task) {
  return task.job?.id ?? task.job_id ?? null;
}

function getTaskJobName(task: Task) {
  return task.job?.name ?? task.job_id ?? '-';
}

function getTaskDeliverableName(task: Task) {
  return task.deliverable?.name ?? task.deliverable_id ?? '-';
}

function reduceAllJobTasks(
  state: AllJobTasksState,
  action: AllJobTasksAction,
): AllJobTasksState {
  if (action.type === 'reset') {
    return {
      page: 1,
      tasks: [],
      nextPage: 1,
      filterKey: action.filterKey,
    };
  }

  if (action.type === 'setPage') {
    return {
      ...state,
      page: action.page,
    };
  }

  if (action.filterKey !== state.filterKey) {
    return state;
  }

  return {
    ...state,
    tasks:
      action.page === 1
        ? action.rows
        : dedupeById([...state.tasks, ...action.rows]),
    nextPage: action.nextPage,
  };
}

export function AllJobTasksList({ search, status }: AllJobTasksListProps) {
  const workspaceRoutes = useWorkspaceRoutes();
  const filterKey = `${search}::${status}`;

  const [state, dispatch] = useReducer(reduceAllJobTasks, {
    page: 1,
    tasks: [],
    nextPage: 1,
    filterKey,
  });

  const currentPage = state.filterKey === filterKey ? state.page : 1;

  const queryArgs = useMemo(
    () => ({
      query: search || undefined,
      task_type: 'job' as const,
      status:
        status !== 'all' && status !== 'draft'
          ? (status as TaskStatus)
          : undefined,
      exclude_status: status === 'all' ? 'archived' : undefined,
      is_draft: status === 'draft' ? true : undefined,
      page: currentPage,
      page_size: PAGE_SIZE,
    }),
    [currentPage, search, status],
  );

  const { data, isLoading, isFetching, error } = useGetTasksQuery(queryArgs, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    dispatch({ type: 'reset', filterKey });
  }, [filterKey]);

  useEffect(() => {
    dispatch({
      type: 'mergePage',
      filterKey,
      page: currentPage,
      rows: data?.data?.results ?? [],
      nextPage: getNextPage(data?.data?.next ?? null),
    });
  }, [currentPage, data, filterKey]);

  useEffect(() => {
    if (!error) {
      return;
    }

    const message =
      error instanceof Error ? error.message : 'Unable to load tasks.';
    notify({ type: 'error', message });
  }, [error]);

  if (!isLoading && !isFetching && !state.tasks.length) {
    return (
      <EmptyState
        title="No job tasks yet."
        description={
          error
            ? error instanceof Error
              ? error.message
              : 'Unable to load tasks.'
            : 'Tasks linked to jobs will appear here once they are created.'
        }
      />
    );
  }

  return (
    <div className={styles.allJobTasksList}>
      <InfiniteTable<Task>
        tableId="all-job-tasks-table"
        columns={[
          {
            id: 'task',
            header: 'TASK',
            accessor: (task) => task.title || '',
            sortable: true,
            minWidth: 250,
            cell: (task) => (
              <div className={styles.taskTitleCell}>
                {getTaskJobId(task) ? (
                  <Link
                    href={workspaceRoutes.jobs.detailTasks(getTaskJobId(task)!)}
                    ariaLabel={`Open job ${getTaskJobName(task)}`}
                  >
                    <span className={styles.taskTitle}>
                      {task.title || 'Untitled task'}
                    </span>
                  </Link>
                ) : (
                  <span className={styles.taskTitle}>
                    {task.title || 'Untitled task'}
                  </span>
                )}
                <div className={styles.taskAssignees}>
                  {(task.assignees ?? []).slice(0, 3).map((assignee) => {
                    const user = assignee.user;
                    if (!user) {
                      return null;
                    }
                    const fullName =
                      `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() ||
                      user.email ||
                      'User';
                    return (
                      <Tooltip key={assignee.id} title={fullName}>
                        <span>
                          <Avatar
                            size="small"
                            name={fullName}
                            src={user.avatar ?? undefined}
                          />
                        </span>
                      </Tooltip>
                    );
                  })}
                </div>
              </div>
            ),
          },
          {
            id: 'job',
            header: 'JOB',
            accessor: (task) => getTaskJobName(task),
            sortable: true,
            minWidth: 210,
            cell: (task) => (
              <span className={styles.taskTextCell}>
                {getTaskJobName(task)}
              </span>
            ),
          },
          {
            id: 'deliverable',
            header: 'DELIVERABLE',
            accessor: (task) => getTaskDeliverableName(task),
            sortable: true,
            minWidth: 190,
            cell: (task) => (
              <span className={styles.taskTextCell}>
                {getTaskDeliverableName(task)}
              </span>
            ),
          },
          {
            id: 'status',
            header: 'STATUS',
            accessor: (task) => getTaskStatusLabel(task),
            sortable: true,
            minWidth: 120,
            cell: (task) => (
              <span className={styles.taskTextCell}>
                {getTaskStatusLabel(task)}
              </span>
            ),
          },
          {
            id: 'due_date',
            header: 'DUE DATE',
            accessor: (task) => task.due_date || '',
            sortable: true,
            minWidth: 110,
            cell: (task) => (
              <span className={styles.taskTextCell}>
                {task.due_date ? formatDueDateLabel(task.due_date) : '-'}
              </span>
            ),
          },
        ]}
        rows={state.tasks}
        isLoading={isLoading && currentPage === 1}
        hasMore={Boolean(state.nextPage)}
        onLoadMore={() => {
          if (state.nextPage && !isFetching) {
            dispatch({ type: 'setPage', page: state.nextPage });
          }
        }}
        getRowId={(row) => row.id}
        rowHeight={68}
        emptyMessage="No job tasks yet."
        virtualizedHeight="auto"
      />
    </div>
  );
}
